import {
  DetalheOpcao,
  DetalheQuestao,
  DetalheSecao
} from "store/modules/licenciamentourbano/actions/obterDetalheTipoRequerimento";

/**
 * PBI 2576 — correção, pelo cidadão, dos campos do formulário que o analista
 * apontou na pendência.
 *
 * O backend grava `RespostaSolicitacao.Value` COMO STRING, sem nenhuma
 * conversão. Então estas funções têm que reproduzir exatamente o formato que o
 * cadastro original produz — senão a correção grava num formato diferente do
 * resto do requerimento.
 */

/**
 * Valores de formulário de UM campo, com as chaves relativas ao `namePrefix`
 * do RHF: a questão em `questao.id` e, no select com sub-opção, o filho em
 * `opcaoPai.id` (é assim que o FormFields nomeia os campos).
 */
export type ValoresCampo = Record<string, unknown>;

const RE_GUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const GUID_ZERO = "00000000-0000-0000-0000-000000000000";

const SEPARADOR_MULTI = " - ";
const SEPARADOR_SUB = ": ";

const normalizar = (texto?: string | null): string =>
  (texto ?? "").trim().toLowerCase();

/**
 * Localiza a questão original a partir do que a pendência informa.
 * `idSecao` pode vir null em resposta legada — aí procura em todas as seções.
 */
export const acharQuestao = (
  secoes: DetalheSecao[],
  idSecao: string | null,
  enunciado: string
): DetalheQuestao | undefined => {
  const alvo = normalizar(enunciado);

  const procurar = (lista: DetalheSecao[]) => {
    for (const secao of lista) {
      const questao = (secao.questoes || []).find(
        (q) => normalizar(q.enunciado) === alvo
      );
      if (questao) return questao;
    }
    return undefined;
  };

  if (idSecao) {
    const naSecao = procurar(secoes.filter((s) => s.id === idSecao));
    if (naSecao) return naSecao;
  }

  return procurar(secoes);
};

const parseSelect = (questao: DetalheQuestao, valor: string): ValoresCampo => {
  if (!valor.trim()) return {};

  // Campo PREFIXADO renderizado como select pelo próprio frontend (UF):
  // FormFields intercepta antes do switch de tipo e usa `ufSelectOptions`, então
  // o valor é a string crua e `opcoes` chega vazio, apesar do tipo select_list.
  if (!questao.opcoes.length) return { [questao.id]: valor };

  // 1) Casamento EXATO primeiro: o texto da própria opção pode conter ": "
  //    (ex.: "Uso misto: comercial e residencial") e o split ingênuo erraria.
  const exata = questao.opcoes.find((o) => o.text === valor);
  if (exata) return { [questao.id]: exata.id };

  // 2) Prefixo mais longo, para não confundir "Outro" com "Outro tipo".
  const pai = questao.opcoes
    .filter((o) => valor.startsWith(o.text + SEPARADOR_SUB))
    .sort((a, b) => b.text.length - a.text.length)[0];

  if (!pai) return {};

  const resto = valor.slice(pai.text.length + SEPARADOR_SUB.length);

  if (pai.tipoOpcao === "item_list") {
    const sub = (pai.opcoes || []).find((s) => s.text === resto);
    return sub
      ? { [questao.id]: pai.id, [pai.id]: sub.id }
      : { [questao.id]: pai.id };
  }

  return { [questao.id]: pai.id, [pai.id]: resto };
};

/**
 * Casamento guloso pelo texto mais longo — um `split(" - ")` simples quebraria
 * quando o texto da opção contém o próprio separador.
 * Devolve [] quando algum pedaço não casa: pré-marcar errado é pior que não
 * pré-marcar.
 */
const parseMulti = (questao: DetalheQuestao, valor: string): string[] => {
  let resto = valor.trim();
  if (!resto) return [];

  const ids: string[] = [];

  while (resto.length) {
    const opcao = questao.opcoes
      .filter(
        (o) => resto === o.text || resto.startsWith(o.text + SEPARADOR_MULTI)
      )
      .sort((a, b) => b.text.length - a.text.length)[0];

    if (!opcao) return [];

    ids.push(opcao.id);
    resto =
      resto === opcao.text
        ? ""
        : resto.slice(opcao.text.length + SEPARADOR_MULTI.length);
  }

  return ids;
};

/**
 * O backend interpola `double` com a CurrentCulture do processo e o host do SIT
 * não configura RequestLocalization — o valor pode chegar com vírgula decimal.
 */
const parseCoordenada = (
  valor: string
): { lat: number; lng: number } | null => {
  const numeros = valor.match(/-?\d+(?:[.,]\d+)?/g);
  if (!numeros || numeros.length < 2) return null;

  const lat = Number(numeros[0].replace(",", "."));
  const lng = Number(numeros[1].replace(",", "."));

  return Number.isNaN(lat) || Number.isNaN(lng) ? null : { lat, lng };
};

/** Converte o valor gravado no banco em valores de formulário. */
export const parseValorAtual = (
  questao: DetalheQuestao,
  valorAtual: string | null
): ValoresCampo => {
  const valor = valorAtual ?? "";

  switch (questao.tipoQuestao) {
    case "select_list":
      return parseSelect(questao, valor);

    case "multi_select":
      return { [questao.id]: parseMulti(questao, valor) };

    case "map": {
      const coordenada = parseCoordenada(valor);
      return coordenada ? { [questao.id]: coordenada } : {};
    }

    default:
      return { [questao.id]: valor };
  }
};

const textoDaSubOpcao = (pai: DetalheOpcao, filho: unknown): string | null => {
  if (filho === undefined || filho === null || filho === "") return null;

  if (pai.tipoOpcao === "item_list") {
    const sub = (pai.opcoes || []).find((s) => s.id === filho);
    return sub ? sub.text : null;
  }

  return String(filho);
};

/**
 * Reproduz o formato que o cadastro original grava:
 *   select_list  -> "Opção"  ou  "Pai: Filho"
 *   multi_select -> textos unidos por " - ", na ordem das opções
 *   map          -> "lat lng" (espaço, latitude primeiro)
 *   demais       -> a própria string do formulário
 */
export const serializarValorCorrigido = (
  questao: DetalheQuestao,
  valores: ValoresCampo
): string => {
  const bruto = valores[questao.id];

  switch (questao.tipoQuestao) {
    case "select_list": {
      // Mesmo caso do parse: sem opções, o valor É o valor. Sem esta guarda a
      // serialização devolveria "" e APAGARIA a resposta do requerente.
      if (!questao.opcoes.length)
        return bruto === undefined || bruto === null ? "" : String(bruto);

      const pai = questao.opcoes.find((o) => o.id === bruto);
      if (!pai) return "";

      const texto = textoDaSubOpcao(pai, valores[pai.id]);
      return texto ? `${pai.text}${SEPARADOR_SUB}${texto}` : pai.text;
    }

    case "multi_select": {
      const ids = Array.isArray(bruto) ? (bruto as string[]) : [];
      if (!ids.length) return "";

      return questao.opcoes
        .filter((o) => ids.includes(o.id))
        .sort((a, b) => (a.value ?? 0) - (b.value ?? 0))
        .map((o) => o.text)
        .join(SEPARADOR_MULTI);
    }

    case "map": {
      const coordenada = bruto as { lat?: number; lng?: number } | undefined;
      if (
        !coordenada ||
        typeof coordenada.lat !== "number" ||
        typeof coordenada.lng !== "number"
      )
        return "";

      return `${coordenada.lat} ${coordenada.lng}`;
    }

    default:
      return bruto === undefined || bruto === null ? "" : String(bruto);
  }
};

/**
 * Serializa as correções no formato que o endpoint aceita.
 *
 * ATENÇÃO — `PUT api/v1/cidadao/licenciamentourbano/pendencia` desserializa
 * `CamposCorrigidos` com o default do System.Text.Json (case-SENSITIVE) contra
 * um record posicional PascalCase. Verificado no backend:
 *   - camelCase       -> id vira Guid.Empty e a API responde 400 "campos não autorizados"
 *   - "valor" ausente -> o handler grava "" e APAGA a resposta do requerente, com 200
 *
 * Este é o ÚNICO ponto do frontend autorizado a montar esse payload.
 * (Gêmeo de camposHelpers.ts no cidax-frontend-colaborador; os repos não
 * compartilham código.)
 */
export const serializarCamposCorrigidos = (
  campos: { idRespostaSolicitacao: string; valor?: string | null }[]
): string =>
  JSON.stringify(
    campos.map((campo) => ({
      IdRespostaSolicitacao: campo.idRespostaSolicitacao,
      Valor: campo.valor ?? ""
    }))
  );

/** O backend descarta GUID malformado em silêncio e responde 200. */
export const filtrarGuidsValidos = (ids: string[]): string[] =>
  ids
    .map((id) => (id ?? "").trim())
    .filter((id) => RE_GUID.test(id) && id.toLowerCase() !== GUID_ZERO)
    .filter((id, indice, lista) => lista.indexOf(id) === indice);
